"use client";

import clsx from "clsx";
import { useSearchParams } from "next/navigation";
import { Suspense, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuthContext } from "@/contexts/common/AuthContextProvider";
import { DEFAULT_BRAND } from "@/enums/brands";
import { useAlert } from "@/hooks/useAlert";
import { BASE_MARKETING_URL, BASE_SUPPORT_URL } from "@/utils/common/baseUrls";
import { ClearAllCache } from "@/utils/common/cache";
import { SetLoginLocation } from "@/utils/common/login";
import {
  EUROPE_REGION,
  getOppositeRegion,
  getOppositeRegionBaseUrl,
} from "@/utils/common/region";
import { useRouter } from "@bprogress/next";
import Button from "../library/Button";
import CircleBackground from "./backgrounds/CircleBackground";
import FancyLoader from "./fancyLoader";
import Loading from "./loading";

function LoginComponent() {
  const router = useRouter();
  const { login, user, signup, logout } = useAuthContext();
  const searchParams = useSearchParams();
  const { setAlert } = useAlert();
  const { t } = useTranslation("login");
  const { t: ct } = useTranslation("common");

  const [showLogout, setShowLogout] = useState<boolean>(false);
  const [brand, setBrand] = useState<string>(DEFAULT_BRAND);
  const [hasBeenLoggedIn, setHasBeenLoggedIn] = useState<boolean>(false);

  const authHint = searchParams.get("gail-auth-hint");
  const oppositeRegion = getOppositeRegion();
  const oppositeRegionBaseUrl = getOppositeRegionBaseUrl();
  const switchRegionPromptKey =
    oppositeRegion === EUROPE_REGION ? "loginToEuInstead" : "loginToGlobalInstead";
  const switchRegionButtonKey =
    oppositeRegion === EUROPE_REGION ? "switchToEu" : "switchToGlobal";

  useEffect(() => {
    try {
      const storedBrand = globalThis.localStorage?.getItem("brand");
      if (storedBrand) setBrand(storedBrand);
    } catch {}

    if (user || !!searchParams.get("error")) {
      return;
    }

    if (globalThis.localStorage) {
      setHasBeenLoggedIn(!!globalThis.localStorage.getItem("orgData"));
    }

    router.push("/");
  }, []);

  const handleLogin = async () => {
    SetLoginLocation(globalThis.location.pathname);
    await login();
  };

  const handleSignUp = async () => {
    await signup();
  };

  const [authHintState, setAuthHintState] = useState<{
    hint: "login" | "signup" | null;
    effect?: () => () => any;
  }>({ hint: null });

  useEffect(() => {
    if (authHintState.hint && authHintState.effect) {
      const effect = authHintState.effect;
      delete authHintState.effect;
      return effect();
    }
  }, [authHintState]);

  useEffect(() => {
    if (authHint && (authHint === "login" || authHint === "signup")) {
      if (authHint === "login") {
        setAuthHintState({
          hint: "login",
          effect() {
            SetLoginLocation(globalThis.location.pathname);
            login();
            return () => {};
          },
        });
      }

      if (authHint === "signup") {
        setAuthHintState({
          hint: "signup",
          effect() {
            SetLoginLocation(globalThis.location.pathname);
            signup();
            return () => {};
          },
        });
      }
    }
  }, [searchParams, authHint]);

  // Get safe, translated error message for security
  const getAllowedErrorMessage = (errorCode: string | null): string => {
    // If we have a specific error code, try to get the translated message
    if (errorCode) {
      const translationKey = `errors.${errorCode}`;
      const translatedMessage = t(translationKey);

      // If translation exists (not the same as the key), use it
      if (translatedMessage !== translationKey) {
        return translatedMessage;
      }
    }

    // If no error code or unknown error code, use the generic translated message
    return t("errors.generic_error");
  };

  // Handle error parameters from Auth0 redirects
  useEffect(() => {
    const errorCode = searchParams.get("error");
    if (errorCode) {
      // Get the safe, translated error message
      const safeMessage = getAllowedErrorMessage(errorCode);
      setAlert({
        message: safeMessage,
        status: "error",
      });
      setShowLogout(true);
      console.error("Login error detected:", { errorCode, safeMessage });
    }
  }, [searchParams]);

  if (authHint) {
    return <Loading />;
  }

  return (
    <div className={clsx("h-screen", "text-800")}>
      <div className="login-logo">
        <div className={clsx("login-mask", "white-mask", "mx-auto", "mb-10", "w-32", "h-32")}></div>
      </div>
      <div className={clsx("fixed", "z-10", "md:top-1/2", "md:-translate-y-1/2")}>
        <div
          className={clsx(
            "flex",
            "flex-col",
            "gap-4",
            "p-6",
            "h-fit",
            "max-h-screen",
            "rounded-t-xl",
            "md:rounded-xl",
            "bg-surface",
            "bg-opacity-80",
            "backdrop-blur-sm",
            "w-screen",
            "fixed",
            "md:relative",
            "bottom-0",
            "md:bottom-auto",
            "md:min-w-auto",
            "md:w-[400px]",
            "md:translate-x-1/4"
          )}
        >
          <div className={clsx("my-auto", "align-middle")}>
            <h1 className={clsx("text-2xl", "text-800", "mb-2", "font-medium")}>
              {t("welcomeToGail")}
            </h1>
            <div className={clsx("text-sm", "text-800", "mb-8")}>{t("tagline")}</div>
            {brand === DEFAULT_BRAND && !showLogout && (
              <>
                <Button
                  type={hasBeenLoggedIn ? "secondary" : "primary"}
                  onClick={handleSignUp}
                  className="!rounded-full"
                  wide
                  testId="signup"
                >
                  {t("createAccount")}
                </Button>
                <div className="relative flex items-center py-4">
                  <div className="flex-grow border-400 border-t"></div>
                  <span className="flex-shrink mx-2 text-600 text-xs">{ct("or")}</span>
                  <div className="flex-grow border-400 border-t"></div>
                </div>
              </>
            )}
            <Button
              className={clsx("!rounded-full", { hidden: showLogout })}
              type={hasBeenLoggedIn ? "primary" : "secondary"}
              onClick={handleLogin}
              testId="login"
              wide
            >
              {t("signIn")}
            </Button>
            {showLogout && (
              <Button
                type="secondary"
                onClick={() => {
                  ClearAllCache();
                  logout();
                }}
                wide
                className={clsx("mt-4", "!rounded-full")}
              >
                {t("logout")}
              </Button>
            )}
          </div>
          <div className={clsx("grid", "grid-cols-2", "gap-2")}>
            <Button
              type="tertiary"
              href={`${BASE_MARKETING_URL}/privacy-policy`}
              target="_blank"
              compact
            >
              {t("privacy")}
            </Button>
            <Button type="tertiary" href={BASE_SUPPORT_URL} target="_blank" compact>
              {t("support")}
            </Button>
          </div>
          <div className={clsx("mt-2", "text-sm", "text-700", "text-center")}>
            {t(switchRegionPromptKey)}
            <Button
              type="tertiary"
              href={oppositeRegionBaseUrl}
              compact
              className={clsx("!inline-block", "text-700", "ml-2")}
            >
              {t(switchRegionButtonKey)}
            </Button>
          </div>
        </div>
      </div>
      <div className={clsx("dark:opacity-50")}>
        <CircleBackground />
      </div>
    </div>
  );
}

export default function Login() {
  return (
    <Suspense fallback={<FancyLoader />}>
      <LoginComponent />
    </Suspense>
  );
}
